import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { SeatPlanCell } from 'features/iam/components/seats/seat-plan-cell';
import type { PlanSeats } from 'features/subscription/utils/seat-pools';

type TeamsPeopleBulkBarProps = {
  count: number;
  planSeats: PlanSeats[];
  disabled: boolean;
  onPick: (entry: PlanSeats) => void;
  onClear: () => void;
};

/** Shown while people are selected: give them all a plan at once, as on the IAM table. */
export function TeamsPeopleBulkBar({
  count,
  planSeats,
  disabled,
  onPick,
  onClear,
}: Readonly<TeamsPeopleBulkBarProps>) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const valueColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-card border border-grey-100 bg-grey-50 px-3 py-2 text-sm leading-body',
        'dark:border-border dark:bg-muted',
        valueColor
      )}
    >
      <span className="font-medium">
        {t('PEOPLE_SELECTED', { count, defaultValue: '{{count}} selected' })}
      </span>
      <SeatPlanCell
        planSeats={planSeats}
        disabled={disabled}
        label={t('GIVE_THEM_A_PLAN', 'Give them a plan')}
        onPick={onPick}
      />
      <button
        type="button"
        onClick={onClear}
        className="inline-flex h-10 items-center rounded-card px-3 font-medium hover:bg-grey-100 md:h-8 dark:hover:bg-card"
      >
        {t('CLEAR', 'Clear')}
      </button>
    </div>
  );
}
